import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Search, Send, CheckCircle2, ExternalLink, RefreshCw, Mail, Euro } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtShort = (d) => new Date(d).toLocaleDateString('it-IT');
const STATUS_META = {
  sent: { label: 'Inviato', cls: 'bg-blue-100 text-blue-700' },
  viewed: { label: 'Visto dal cliente', cls: 'bg-indigo-100 text-indigo-700' },
  requested: { label: 'Vuole prenotare', cls: 'bg-amber-100 text-amber-700' },
  booked: { label: 'Prenotato ✓', cls: 'bg-emerald-100 text-emerald-700' },
  lost: { label: 'Perso', cls: 'bg-slate-100 text-slate-500' },
  expired: { label: 'Scaduto', cls: 'bg-red-100 text-red-600' },
};
const PETS_BADGE = {
  allowed: { label: '🐾 animali ammessi', cls: 'bg-emerald-100 text-emerald-700' },
  on_request: { label: '🐾 su richiesta', cls: 'bg-amber-100 text-amber-800' },
  unknown: { label: '🐾 da verificare', cls: 'bg-yellow-100 text-yellow-800' },
};
const TRAVEL_LABELS = { leisure: 'Vacanza', work: 'Lavoro', study: 'Studio', health: 'Salute', other: 'Altro' };

function NewQuoteTab({ token, onSent }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', check_in: '', check_out: '', guests: 2, zone_query: '', children_under_10: 0, pets: false, travel_reason: 'leisure' });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState([]);
  const [frequency, setFrequency] = useState('');
  const [sending, setSending] = useState(false);
  const f = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const nights = form.check_in && form.check_out ? Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000) : 0;
  const inputCls = 'w-full px-3 py-2 border rounded-lg text-sm';

  const search = async () => {
    if (!form.check_in || !form.check_out) return toast.error('Inserisci le date del soggiorno');
    setSearching(true); setResults(null);
    try {
      const res = await axios.post(`${API}/api/partner-quotes/${token}/search`, {
        check_in: form.check_in, check_out: form.check_out, guests: Number(form.guests) || 2,
        zone_query: form.zone_query, children_under_10: Number(form.children_under_10) || 0, pets: !!form.pets,
      });
      setResults(res.data);
      setSelected(res.data.suggested || []);
      if (res.data.zone_note) toast.info(res.data.zone_note);
      if (!res.data.count) toast.warning(res.data.empty_reason || 'Nessun immobile disponibile per zona/date richieste', { duration: 15000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore ricerca'); }
    finally { setSearching(false); }
  };

  const toggle = (pid) => setSelected(sel =>
    sel.includes(pid) ? sel.filter(x => x !== pid) : (sel.length >= 3 ? (toast.warning('Massimo 3 immobili per preventivo'), sel) : [...sel, pid]));

  const send = async () => {
    if (!form.first_name || !form.last_name || !form.email) return toast.error('Compila nome, cognome ed email del cliente');
    if (!selected.length) return toast.error('Seleziona almeno un immobile');
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/partner-quotes/${token}/quotes`, {
        client: { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone },
        check_in: form.check_in, check_out: form.check_out, guests: Number(form.guests) || 2,
        zone_query: form.zone_query, property_ids: selected,
        children_under_10: Number(form.children_under_10) || 0, pets: !!form.pets, travel_reason: form.travel_reason || null,
        top_property_id: results?.top_property_id && selected.includes(results.top_property_id) ? results.top_property_id : null,
        zone_fallback: !!results?.nearby_fallback,
        periodic_frequency: nights >= 28 && frequency ? frequency : null,
      });
      toast.success(res.data.message + ' — riceverai una copia dell\'email');
      setResults(null); setSelected([]);
      setForm({ first_name: '', last_name: '', email: '', phone: '', check_in: '', check_out: '', guests: 2, zone_query: '', children_under_10: 0, pets: false, travel_reason: 'leisure' });
      onSent();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio preventivo'); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="bg-white rounded-xl border p-5 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><label className="text-xs font-medium text-slate-600">Nome *</label><input className={inputCls} value={form.first_name} onChange={e => f('first_name', e.target.value)} data-testid="partner-quote-first-name" /></div>
          <div><label className="text-xs font-medium text-slate-600">Cognome *</label><input className={inputCls} value={form.last_name} onChange={e => f('last_name', e.target.value)} data-testid="partner-quote-last-name" /></div>
          <div><label className="text-xs font-medium text-slate-600">Email *</label><input type="email" className={inputCls} value={form.email} onChange={e => f('email', e.target.value)} data-testid="partner-quote-email" /></div>
          <div><label className="text-xs font-medium text-slate-600">Telefono</label><input className={inputCls} value={form.phone} onChange={e => f('phone', e.target.value)} data-testid="partner-quote-phone" /></div>
          <div><label className="text-xs font-medium text-slate-600">Check-in *</label><input type="date" className={inputCls} value={form.check_in} onChange={e => f('check_in', e.target.value)} data-testid="partner-quote-check-in" /></div>
          <div><label className="text-xs font-medium text-slate-600">Check-out *</label><input type="date" className={inputCls} value={form.check_out} onChange={e => f('check_out', e.target.value)} data-testid="partner-quote-check-out" /></div>
          <div><label className="text-xs font-medium text-slate-600">Ospiti</label><input type="number" min="1" className={inputCls} value={form.guests} onChange={e => f('guests', e.target.value)} data-testid="partner-quote-guests" /></div>
          <div><label className="text-xs font-medium text-slate-600">di cui bimbi &lt;10 anni (soggiorno gratis)</label><input type="number" min="0" className={inputCls} value={form.children_under_10} onChange={e => f('children_under_10', e.target.value)} data-testid="partner-quote-children" /></div>
          <div><label className="text-xs font-medium text-slate-600">Motivo del viaggio</label>
            <select className={inputCls + ' bg-white'} value={form.travel_reason} onChange={e => f('travel_reason', e.target.value)} data-testid="partner-quote-travel-reason">
              {Object.entries(TRAVEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex items-end pb-1"><label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={form.pets} onChange={e => f('pets', e.target.checked)} data-testid="partner-quote-pets" /> 🐾 Viaggia con animali</label></div>
          <div><label className="text-xs font-medium text-slate-600">Zona / immobile (ricerca libera)</label><input className={inputCls} placeholder='es. "Chiavari", "riviera di levante"...' value={form.zone_query} onChange={e => f('zone_query', e.target.value)} data-testid="partner-quote-zone" /></div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={search} disabled={searching} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="partner-quote-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Cerca disponibilità
          </button>
          {nights > 0 && <span className="text-xs text-slate-500">{nights} notti</span>}
          {nights >= 28 && (
            <select value={frequency} onChange={e => setFrequency(e.target.value)} className="px-2 py-1.5 border rounded-lg text-sm bg-white" data-testid="partner-quote-frequency">
              <option value="">Pulizie periodiche: nessuna</option>
              <option value="weekly">Pulizia settimanale + biancheria</option>
              <option value="biweekly">Pulizia bisettimanale + biancheria</option>
              <option value="monthly">Pulizia mensile + biancheria</option>
            </select>
          )}
        </div>
      </div>

      {results && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="partner-quote-results">
          {results.nearby_fallback && (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded-lg p-3" data-testid="partner-nearby-banner">
              ⚠️ Nessun immobile disponibile nella zona cercata: ti mostriamo <b>i più vicini</b>. Nell'email il cliente verrà avvisato che sono le migliori alternative in zona.
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-slate-700">{results.count} immobili disponibili — ne abbiamo già selezionati 3 per te, puoi cambiarli liberamente</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{selected.length}/3 selezionati</span>
              <button onClick={send} disabled={sending || !selected.length} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="partner-quote-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia preventivo
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.results.map(r => {
              const isSel = selected.includes(r.property_id);
              return (
                <button key={r.property_id} onClick={() => toggle(r.property_id)}
                  className={`text-left border-2 rounded-xl overflow-hidden transition-colors ${isSel ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-slate-200'}`}
                  data-testid={`partner-quote-result-${r.property_code}`}>
                  <div className="relative h-36 bg-slate-100">
                    {r.cover_url && <img src={r.cover_url} alt={r.name} className="w-full h-full object-cover" />}
                    {isSel && <span className="absolute top-2 right-2 bg-blue-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">✓ Selezionato</span>}
                  </div>
                  <div className="p-3 bg-white">
                    <p className="font-semibold text-sm truncate">{r.name}</p>
                    <p className="text-xs text-slate-400">{r.city}{r.min_stay_warning && <span className="text-red-500"> · min stay!</span>}</p>
                    {r.pets_status && PETS_BADGE[r.pets_status] && (
                      <span className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${PETS_BADGE[r.pets_status].cls}`}>{PETS_BADGE[r.pets_status].label}</span>
                    )}
                    <p className="mt-1 font-bold text-blue-700">{r.monthly_total ? `${eur(r.monthly_total)}/mese` : eur(r.total)}</p>
                    {r.monthly_total && <p className="text-[11px] text-slate-500">Totale {r.accommodation.nights} notti: {eur(r.total)}</p>}
                    <p className="text-[11px] text-slate-400">Soggiorno {eur(r.accommodation.accommodation_total)} + pulizia {eur(r.fees.cleaning)} + biancheria {eur(r.fees.linen)}</p>
                    {r.tourist_tax && <p className="text-[11px] text-amber-700">+ tassa soggiorno {eur(r.tourist_tax.amount)} in loco</p>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function ArchiveTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/partner-quotes/${token}/quotes`).then(res => setData(res.data))
      .catch(() => toast.error('Errore caricamento')).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const resend = async (q) => {
    if (!window.confirm(`Reinviare l'email al cliente ${q.client.email}?`)) return;
    try {
      const res = await axios.post(`${API}/api/partner-quotes/${token}/quotes/${q.id}/resend-email`);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore reinvio'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
  const quotes = data?.quotes || [];
  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="partner-quotes-archive">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold text-slate-700">{quotes.length} preventivi inviati</p>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold px-3 py-1.5 rounded-full" data-testid="partner-commissions-total">
            <Euro className="h-4 w-4" /> Compensi maturati: {eur(data?.commissions_total || 0)}
          </span>
          <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Aggiorna" data-testid="partner-archive-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {!quotes.length ? <p className="text-sm text-slate-400 py-6 text-center">Nessun preventivo ancora: crea il primo dal tab "Nuovo preventivo"!</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-xs text-slate-500">
              <th className="text-left py-2">Cliente</th><th className="text-left py-2">Soggiorno</th>
              <th className="text-left py-2">Immobili</th><th className="text-left py-2">Stato</th>
              <th className="text-left py-2">Pagamento</th><th className="text-left py-2">Azioni</th>
            </tr></thead>
            <tbody>
              {quotes.map(q => {
                const st = STATUS_META[q.status] || { label: q.status, cls: 'bg-slate-100' };
                return (
                  <tr key={q.id} className="border-b last:border-0 align-top" data-testid={`partner-quote-row-${q.id}`}>
                    <td className="py-2.5 pr-2">
                      <p className="font-medium">{q.client.first_name} {q.client.last_name}</p>
                      <p className="text-xs text-slate-400">{q.client.email}</p>
                    </td>
                    <td className="py-2.5 pr-2 whitespace-nowrap text-xs">{fmtShort(q.check_in)} → {fmtShort(q.check_out)}<br />{q.guests} ospiti{q.pets ? ' 🐾' : ''}</td>
                    <td className="py-2.5 pr-2 text-xs">{q.properties.map(p => <p key={p.property_code}>{p.name} — <b>{eur(p.total)}</b></p>)}</td>
                    <td className="py-2.5 pr-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span></td>
                    <td className="py-2.5 pr-2 text-xs">
                      {q.payment ? (<><p className="font-semibold text-emerald-700">{q.payment.booking_code}</p><p>Incassato: {eur(q.payment.paid_now)}</p></>) : '—'}
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1.5">
                        <a href={q.public_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Apri pagina preventivo" data-testid={`partner-quote-open-${q.id}`}><ExternalLink className="h-4 w-4" /></a>
                        <button onClick={() => resend(q)} className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Reinvia email al cliente" data-testid={`partner-quote-resend-${q.id}`}><Mail className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {(data?.commissions || []).length > 0 && (
        <div className="border-t pt-3">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">I tuoi compensi (10% sull'incassato della locazione)</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.commissions.map(c => (
              <div key={c.id} className="flex justify-between text-xs text-slate-600" data-testid={`partner-commission-${c.id}`}>
                <span>{fmtShort(c.created_at)} · {c.booking_code || c.quote_id.slice(0, 8)} · {c.event.replace(/_/g, ' ')}</span>
                <b className="text-emerald-700">+{eur(c.commission)}</b>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PartnerQuotesPage() {
  const { token } = useParams();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('new');

  useEffect(() => {
    axios.get(`${API}/api/partner-quotes/${token}/me`)
      .then(res => setMe(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Link non valido'));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow p-8 text-center max-w-md" data-testid="partner-link-error">
        <p className="text-lg font-bold text-slate-800 mb-2">Link non valido</p>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );
  if (!me) return <div className="min-h-screen bg-slate-100 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="partner-quotes-page">
      <Toaster position="top-center" richColors />
      <div className="bg-[#1d4ed8] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-200 font-bold">Holiday World · Preventivi Partner</p>
            <h1 className="text-2xl font-bold mt-0.5" data-testid="partner-name">Ciao, {me.name || me.code}!</h1>
            <p className="text-sm text-blue-100 mt-0.5">Crea e invia preventivi ai tuoi clienti: sui preventivi pagati maturi il <b>{me.commission_pct}%</b> della locazione incassata.</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="bg-white/10 rounded-xl px-4 py-2 text-center"><p className="text-xs text-blue-200">Preventivi</p><p className="font-bold text-lg" data-testid="partner-quotes-count">{me.quotes_count}</p></div>
            <div className="bg-white/10 rounded-xl px-4 py-2 text-center"><p className="text-xs text-blue-200">Compensi maturati</p><p className="font-bold text-lg" data-testid="partner-header-commissions">{eur(me.commissions_total)}</p></div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 py-5 space-y-4">
        <div className="flex bg-white border p-1 rounded-lg w-fit">
          <button onClick={() => setTab('new')} className={`px-4 py-1.5 text-sm font-semibold rounded-md ${tab === 'new' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="partner-tab-new">Nuovo preventivo</button>
          <button onClick={() => setTab('archive')} className={`px-4 py-1.5 text-sm font-semibold rounded-md ${tab === 'archive' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="partner-tab-archive">I miei preventivi</button>
        </div>
        {tab === 'new' ? <NewQuoteTab token={token} onSent={() => setTab('archive')} /> : <ArchiveTab token={token} />}
        <p className="text-center text-xs text-slate-400 pb-4">Holiday World · The best Holiday Home · Questo link è personale: non condividerlo.</p>
      </div>
    </div>
  );
}
